import type { CSSObject } from '@mui/system'

/**
 * ─────────────────────────────────────────────────────────────────────────────
 * Overrides responsive para modales (MUI Joy + MUI Material)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * PROBLEMA QUE RESUELVE
 * En pantallas de poca altura (tablets en horizontal, laptops de 768px con la
 * barra del navegador, móviles apaisados) los botones de acción de los modales
 * quedaban recortados fuera del diálogo, invisibles y SIN posibilidad de hacer
 * scroll hasta ellos.
 *
 * CAUSA RAÍZ (verificada en el código de la librería)
 * `@mui/joy/ModalDialog` define `maxHeight: calc(100% - 2*--Card-padding)` pero
 * NO define `overflow`. Cuando el contenido excede esa altura el navegador lo
 * recorta en lugar de generar scroll — y el `<body>` está bloqueado por el
 * backdrop del Modal, así que no hay ningún scroll de rescate.
 *
 * ESTRATEGIA
 *  1. Acotar SIEMPRE el diálogo al viewport (`dvh`/`vw`, no `vh`: en móvil la
 *     barra de URL retráctil hace que `100vh` sea mayor que el área visible).
 *  2. Dar al propio diálogo `overflow-y: auto` como red de seguridad, para que
 *     el footer de botones sea alcanzable aunque el modal no tenga un layout
 *     header/body/footer bien construido.
 *  3. Por debajo de `sm` (600px) el modal pasa a pantalla completa, que es el
 *     estándar de la industria en móvil y maximiza el espacio útil del form.
 *
 * NOTA SOBRE PRECEDENCIA
 * El `sx` de cada instancia gana sobre estos overrides de theme (Emotion inserta
 * la clase del `sx` después). Por eso el bloque móvil usa `!important` sobre las
 * propiedades de ancho: hay ~33 modales con `sx={{ minWidth: 600 }}` en px fijo
 * que, sin esto, seguirían desbordando horizontalmente el viewport del móvil.
 * Es intencional y acotado exclusivamente al breakpoint móvil.
 *
 * Los modales que YA implementan bien el patrón (`p: 0` + `overflow: 'hidden'` +
 * flex column con body scrolleable y footer fijo — p. ej. `pages/QuickReplies`
 * y `pages/CommentAutoReplyCampaigns`) no se ven afectados: su `overflow:
 * 'hidden'` en `sx` gana sobre el `overflowY: 'auto'` de aquí.
 */

/** Margen de respiro entre el borde del diálogo y el borde del viewport (px). */
export const MODAL_VIEWPORT_GUTTER = 16

/** Breakpoint por debajo del cual el modal pasa a pantalla completa. */
export const MODAL_FULLSCREEN_MAX_WIDTH = 599.95

const MOBILE_QUERY = `@media (max-width: ${MODAL_FULLSCREEN_MAX_WIDTH}px)`
const GUTTER_X2 = MODAL_VIEWPORT_GUTTER * 2

/**
 * Estilos responsive para `JoyModalDialog.styleOverrides.root`.
 * Se comparte entre chateamTheme, facebookTheme y whatsappTheme.
 */
export const joyModalDialogResponsive: CSSObject = {
  // ── Acotar al viewport ────────────────────────────────────────────────────
  maxHeight: `calc(100dvh - ${GUTTER_X2}px)`,
  maxWidth: `calc(100vw - ${GUTTER_X2}px)`,

  // ── Red de seguridad: el diálogo siempre puede scrollear ──────────────────
  overflowY: 'auto',
  overflowX: 'hidden',
  overscrollBehavior: 'contain',
  WebkitOverflowScrolling: 'touch',

  // ── Móvil: pantalla completa ──────────────────────────────────────────────
  [MOBILE_QUERY]: {
    // `!important` deliberado en TODAS las propiedades de dimensión: el `sx` de
    // cada instancia se inserta después y ganaría por orden de cascada. Hay
    // ~21 modales que declaran su propio `width` (p. ej. `{ xs: 'calc(100vw -
    // 24px)' }`) y decenas con `maxHeight: '90vh'`; sin `!important` seguirían
    // dejando una franja de backdrop a la derecha y un hueco abajo en lugar de
    // ocupar la pantalla completa.
    width: '100% !important',
    minWidth: '0 !important',
    maxWidth: '100% !important',
    height: '100dvh !important',
    maxHeight: '100dvh !important',
    // Joy centra el diálogo con position absolute + translate(-50%, -50%);
    // hay que neutralizarlo para que ocupe la pantalla completa.
    top: 0,
    left: 0,
    right: 'auto',
    bottom: 'auto',
    transform: 'none',
    margin: 0,
    borderRadius: 0,
    // Respeta la barra de gestos de iOS / Android.
    paddingBottom: 'calc(var(--ModalDialog-padding, 1.25rem) + env(safe-area-inset-bottom))',
  },
}

/**
 * Estilos responsive para `MuiDialog.styleOverrides.paper` (@mui/material).
 * Afecta a los 12 modales de FlowBuilder, los únicos que usan Material Dialog.
 */
export const muiDialogPaperResponsive: CSSObject = {
  maxHeight: `calc(100dvh - ${GUTTER_X2}px)`,
  // El paper es flex column: header (auto) + content (flex:1, scroll) + actions (auto).
  display: 'flex',
  flexDirection: 'column',

  [MOBILE_QUERY]: {
    // Mismo criterio que en Joy: los 11 modales de FlowBuilder aplican su
    // `maxWidth` en px mediante `styled(Dialog)` sobre `& .MuiDialog-paper`,
    // que tiene más especificidad que este `styleOverrides`.
    margin: 0,
    width: '100% !important',
    maxWidth: '100% !important',
    height: '100dvh !important',
    maxHeight: '100dvh !important',
    borderRadius: 0,
  },
}

/**
 * `MuiDialogContent`: garantiza que el área central sea la que scrollea.
 * `minHeight: 0` es imprescindible — sin él un hijo flex se niega a encogerse
 * por debajo de su contenido y empuja al footer fuera del paper.
 */
export const muiDialogContentResponsive: CSSObject = {
  overflowY: 'auto',
  minHeight: 0,
  flex: '1 1 auto',
}

/**
 * `MuiDialogActions`: footer pegado al fondo y SIEMPRE visible.
 * `position: sticky` cubre el caso en el que el paper acabe scrolleando entero.
 */
export const muiDialogActionsResponsive: CSSObject = {
  position: 'sticky',
  bottom: 0,
  zIndex: 2,
  flexShrink: 0,
  gap: 8,
  backgroundColor: 'var(--mui-palette-background-paper, #fff)',
  borderTop: '1px solid var(--mui-palette-divider, rgba(0, 0, 0, 0.12))',
  paddingBottom: 'calc(8px + env(safe-area-inset-bottom))',
}
